import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, User, AtSign, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface EmailForm {
  name: string;
  email: string;
  message: string;
}

interface EmailChatTabProps {
  onClose?: () => void;
}

const EmailChatTab: React.FC<EmailChatTabProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailForm, setEmailForm] = useState<EmailForm>({
    name: '',
    email: '',
    message: ''
  });

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!emailForm.name || !emailForm.email || !emailForm.message) {
      toast.error(t('chat.email.validation_error', 'Compila tutti i campi richiesti'));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log("Sending email via API:", { ...emailForm, language: i18n.language });
      
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language || 'it'
        },
        body: JSON.stringify({
          ...emailForm,
          language: i18n.language,
          to: "victor@victorlazari.com",
          subject: `${t('chat.email.subject', 'Nuovo Messaggio dal Sito Web')} - ${emailForm.name}`,
          replyTo: emailForm.email
        })
      });
      
      // Check if response is ok before parsing
      if (!response.ok) {
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log("Email API response:", result);
      
      if (result.success) {
        toast.success(t('chat.email.success', 'Messaggio inviato con successo'));
        setEmailForm({
          name: '',
          email: '',
          message: ''
        });
        if (onClose) {
          setTimeout(onClose, 2000);
        }
      } else {
        toast.error(result.message || t('chat.email.error', 'Errore nell\'invio del messaggio'));
        console.error('Email sending failed:', result);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(
        errorMessage.includes('Missing') || errorMessage.includes('credentials')
          ? t('chat.email.config_error', 'Errore di configurazione: verifica le credenziali email')
          : errorMessage || t('chat.email.error', 'Errore nell\'invio del messaggio')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEmailForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <form onSubmit={handleEmailSubmit} className="flex flex-col h-full p-5 bg-[#04060b]">
      
      <div className="mb-5 text-center">
        <h3 className="text-lg font-bold text-white mb-2 leading-tight font-mono tracking-wide">
          {t('chat.modes.email.title')}
        </h3>
        <p className="text-slate-400 text-sm leading-relaxed font-light">
          {t('chat.modes.email.description')}
        </p>
      </div>
      
      <div className="space-y-5 flex-1 overflow-y-auto pb-4 px-1 custom-scrollbar">
        <div className="relative">
          <label className="block text-slate-500 text-[10px] font-mono uppercase tracking-wider mb-1.5">
            {t('chat.modes.email.form.name')}
          </label>
          <div className="relative">
            <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              name="name"
              value={emailForm.name}
              onChange={handleEmailChange}
              required
              className="w-full bg-black/40 border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/50 focus:bg-black/60 transition-all text-sm font-light"
            />
          </div>
        </div>
        
        <div className="relative">
          <label className="block text-slate-500 text-[10px] font-mono uppercase tracking-wider mb-1.5">
            {t('chat.modes.email.form.email')}
          </label>
          <div className="relative">
            <AtSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="email"
              name="email"
              value={emailForm.email}
              onChange={handleEmailChange}
              required
              className="w-full bg-black/40 border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/50 focus:bg-black/60 transition-all text-sm font-light"
            />
          </div>
        </div>
        
        <div className="relative">
          <label className="block text-slate-500 text-[10px] font-mono uppercase tracking-wider mb-1.5">
            {t('chat.modes.email.form.message')}
          </label>
          <div className="relative">
            <FileText className="w-4 h-4 absolute left-3 top-4 text-slate-500" />
            <textarea
              name="message"
              value={emailForm.message}
              onChange={handleEmailChange}
              required
              rows={5}
              className="w-full bg-black/40 border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-white placeholder-slate-700 resize-none focus:outline-none focus:border-cyan-500/50 focus:bg-black/60 transition-all text-sm font-light"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end relative">
        <button
          type="submit"
          className="p-3 bg-cyan-600 rounded-xl text-white transition-all focus:outline-none disabled:opacity-50 disabled:pointer-events-none self-end shadow-lg shadow-cyan-500/10 min-w-[50px] min-h-[50px] flex items-center justify-center hover:bg-cyan-500 hover:shadow-cyan-500/30 hover:-translate-y-0.5"
          disabled={!emailForm.name.trim() || !emailForm.email.trim() || !emailForm.message.trim() || isSubmitting}
        >
          {isSubmitting ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>
    </form>
  );
};

export default EmailChatTab; 